import TripModule from '../TripModule';
import { TrainFilters, type TrainFiltersData } from './TrainModuleTypes';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Train from '@itinerary/trip/train/Train';
import type TrainPackage from '@itinerary/trip/train/TrainPackage';
import type TrainPriceClass from '@itinerary/trip/train/TrainPriceClass';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import {
    type Point,
    TimeFilter,
    type TripShareInfo,
    TripSortBy,
} from '@itinerary/trip/TripModuleTypes';
import {
    ButtonGroupFilter,
    CheckboxFilter,
    type EmptyListReason,
    type Filter,
    PriceFilter,
} from '@itinerary/ItineraryModuleTypes';
import {
    SellTypeCode,
    type TrainLocation,
    type TrainUrlParams,
} from '@utils/train';
import { TripType } from '@utils/trip';
import { ReserveType } from '@utils/itinerary';
import type TrainTourPackage from '@itinerary/trip/train/TrainTourPackage';

import { type TrainPackageLoaderConfig } from '@itinerary/trip/train/types';
import TrainPackageSet from '@itinerary/trip/train/TrainPackageSet';
import {
    GetAvailableStatus,
    type MidStationResponse,
    TrainApiApiGetNearbyRoutesPost,
    TrainApiApiMidStationTrainCapacityIdGet,
} from '@clients/TrainApi';

export default class TrainModule extends TripModule<
    Train,
    TrainPriceClass,
    TrainFiltersData,
    TrainLocation,
    TrainPackage | TrainTourPackage
> {
    override descriptionTemplate =
        'خرید اینترنتی بلیط قطار (مبدا) (مقصد). بلیط قطار سریع‌السیر و کوپه‌ای و برنامه حرکت قطارها. ابتدا وارد وب‌سایت شوید. قطارها را جستجو سپس بلیط قطار (مبدا) (مقصد) را به صورت آنلاین رزرو کنید. مِستربلیط سایت و اپلیکیشن رزرو اینترنتی بلیط قطار با ارزان‌ترین قیمت و بدون نیاز به ثبت نام';

    override get defaultSortBy(): TripSortBy {
        return TripSortBy.Time;
    }

    override get reserveInfo() {
        return useTrainForm().reserveInfo;
    }

    override getPackageSet(
        loaderConfig: TrainPackageLoaderConfig
    ): TrainPackageSet {
        return new TrainPackageSet(loaderConfig, this.getInitialFilters());
    }

    override applyFilter(
        pkg: TripPackage<Train, TripPriceClass>,
        filters: { [key in TrainFiltersData['key']]: any }
    ): boolean {
        if (!pkg.segments.length) {
            return false;
        }

        const [train] = pkg.segments[0].trips;
        let price: TrainPriceClass | null | undefined =
            pkg.defaultPrice as TrainPriceClass;
        if (!train) {
            return false;
        }
        if (!price) {
            price = pkg.selectedPrices?.[0] as TrainPriceClass;
        }
        if (!price) {
            return false;
        }
        const departureMinutes = timeToMinutes(train.departureTime);
        const arrivalMinutes =
            train.arrivalTime && timeToMinutes(train.arrivalTime);
        return (
            (!filters.corporation ||
                filters.corporation.length === 0 ||
                filters.corporation.some((a: any) =>
                    pkg.segments.some(s =>
                        s.trips.some(f =>
                            `,${a},`.includes(`,${f.corporationInfo.code},`)
                        )
                    )
                )) &&
            (!filters.price ||
                (pkg.singlePayable >= filters.price[0] &&
                    pkg.singlePayable <= filters.price[1])) &&
            (!filters.time ||
                !filters.time.departure ||
                filters.time.departure.length === 0 ||
                (departureMinutes <= 8 * 60 &&
                    filters.time.departure.includes('midnight')) ||
                (departureMinutes >= 8 * 60 &&
                    departureMinutes <= 18 * 60 &&
                    filters.time.departure.includes('day')) ||
                (departureMinutes >= 18 * 60 &&
                    filters.time.departure.includes('night'))) &&
            (!filters.time ||
                !filters.time.arrival ||
                filters.time.arrival.length === 0 ||
                (+arrivalMinutes <= 8 * 60 &&
                    filters.time.arrival.includes('midnight')) ||
                (+arrivalMinutes >= 8 * 60 &&
                    +arrivalMinutes <= 18 * 60 &&
                    filters.time.arrival.includes('day')) ||
                (+arrivalMinutes >= 18 * 60 &&
                    filters.time.arrival.includes('night'))) &&
            (!filters.wagonType ||
                filters.wagonType.length === 0 ||
                (!price.isCompartment && filters.wagonType.includes('bus')) ||
                (price.isCompartment &&
                    price.compartmentCapacity === 4 &&
                    filters.wagonType.includes('4p')) ||
                (price.isCompartment &&
                    price.compartmentCapacity === 6 &&
                    filters.wagonType.includes('6p'))) &&
            (!filters.hotel ||
                filters.hotel.length === 0 ||
                (price.hasHotel && filters.hotel.includes('true')) ||
                (!price.hasHotel && filters.hotel.includes('false')))
        );
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return new TaxiLateralLoader();
    }

    override get emptyListReason(): EmptyListReason {
        if (this.hasPreSell) {
            return {
                image: '/img/no-result/pre-sell.png',
                title: '',
                notFoundText:
                    'خرید بلیط قطار برای تاریخ انتخابی شما در پیش فروش بعدی راه آهن امکان‌پذیر است!',
                idOfElement: 'train-presell',
            };
        }
        return super.emptyListReason;
    }

    override get haveTracking() {
        const trainForm = useTrainForm();
        if (trainForm.sellType.code === SellTypeCode.Car) {
            return false;
        } else {
            return super.haveTracking;
        }
    }

    override get originLocation(): Point | null {
        const trainForm = useTrainForm();
        if (super.originLocation) {
            return super.originLocation;
        }
        const origin = trainForm.trainStations.find(
            station => station.code.toString() === trainForm.originCode
        );
        if (origin?.latitude && origin.longitude) {
            return origin as Point;
        }
        return null;
    }

    override get destinationLocation(): Point | null {
        const trainForm = useTrainForm();
        if (super.destinationLocation) {
            return super.destinationLocation;
        }
        const destination = trainForm.trainStations.find(
            station => station.code.toString() === trainForm.destinationCode
        );
        if (destination?.latitude && destination.longitude) {
            return destination as Point;
        }
        return null;
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet?.filterMetaData) {
            return [];
        }
        const trainForm = useTrainForm();

        const filterData = this.currentPackageSet.filterMetaData;
        return this.sortFilters([
            new PriceFilter(
                TrainFilters.price,
                filterData.minPrice ?? 0,
                filterData.maxPrice ?? 0
            ),
            new TimeFilter(
                TrainFilters.time,
                trainForm.origin?.title ?? '',
                trainForm.destination?.title ?? ''
            ),
            new ButtonGroupFilter(
                TrainFilters.wagonType,
                [
                    {
                        id: '4p',
                        title: '۴ نفره',
                    },
                    {
                        id: '6p',
                        title: '۶ نفره',
                    },
                    {
                        id: 'bus',
                        title: 'سالنی',
                    },
                ],
                'نوع کوپه'
            ),
            new ButtonGroupFilter(
                TrainFilters.hotel,
                [
                    {
                        id: 'true',
                        title: 'با هتل',
                    },
                    {
                        id: 'false',
                        title: 'بدون هتل',
                    },
                ],
                'هتل'
            ),
            new CheckboxFilter(
                TrainFilters.corporation,
                filterData.corporations?.map(c => ({
                    id: c.ids?.toString() ?? '',
                    title: c.name ?? '',
                })) ?? [],
                'شرکت‌های مسافربری'
            ),
        ]);
    }

    override itineraryPackageSets: (TrainPackageSet | null)[] = [];

    override async loadSuggestions(params: TripShareInfo) {
        const itineraryPackageSet =
            this.itineraryPackageSets?.[this.currentSetIndex];
        if (itineraryPackageSet) {
            params.adultCount = itineraryPackageSet.adultCount;
            params.childCount = itineraryPackageSet.childCount;
            params.infantCount = itineraryPackageSet.infantCount;
        }

        try {
            const { data } = await TrainApiApiGetNearbyRoutesPost(
                {
                    getNearbyRoutesRequest: {
                        from: {
                            latitude: params.origin.latitude,
                            longitude: params.origin.longitude,
                        },
                        to: {
                            latitude: params.destination.latitude,
                            longitude: params.destination.longitude,
                        },
                        departureDate: dayConfig
                            .gregory(params.departureDate)
                            .format(formatter()),
                    },
                },
                {
                    timeout: 10000,
                }
            );

            const route = data.routes?.[0];
            const trainForm = useTrainForm();
            if (!route?.fromID || !route.toID) {
                this.setPackageSet({
                    index: this.currentSetIndex,
                    set: null,
                });
                return;
            }
            const originCode = await trainForm.findCodeBaseOnStationId(
                route.fromID
            );
            const destinationCode = await trainForm.findCodeBaseOnStationId(
                route.toID
            );
            if (!originCode || !destinationCode) {
                this.setPackageSet({
                    index: this.currentSetIndex,
                    set: null,
                });
                return;
            }
            trainForm.setOrigin({
                code: originCode,
                title: route.fromName ?? '',
                stationId: route.fromID,
            });
            trainForm.setDestination({
                code: destinationCode,
                title: route.toName ?? '',
                stationId: route.toID,
            });
            trainForm.tripType = params.tripType;
            const origin = await trainForm.findCodeBaseOnStationId(
                this.isSelectingReturn ? route?.toID : route?.fromID
            );
            const destination = await trainForm.findCodeBaseOnStationId(
                this.isSelectingReturn ? route?.fromID : route?.toID
            );
            const url = trainForm.generateViewUrl({
                origin,
                destination,
                departureDate: dayConfig
                    .jalali(params.departureDate)
                    .format(formatter()),
                returnDate:
                    params.tripType === TripType.RoundTrip
                        ? dayConfig
                              .jalali(params.returnDate)
                              .format(formatter())
                        : undefined,
                adultCount: params.adultCount.toString(),
                childCount: params.childCount.toString(),
                infantCount: params.infantCount.toString(),
                sellTypeCode: SellTypeCode.Family.toString(),
                exclusive: '0',
            });

            this.setPackageSet({
                index: this.currentSetIndex,
                set: !route
                    ? null
                    : new TrainPackageSet(
                          {
                              originCode: route.fromID.toString(),
                              destinationCode: route.toID.toString(),
                              date: this.isSelectingReturn
                                  ? params.returnDate!
                                  : params.departureDate!,
                              adultCount: params.adultCount,
                              childCount: params.childCount,
                              infantCount: params.infantCount,
                              sellType: SellTypeCode.Family,
                              exclusive: false,
                              url,
                              originName: route.fromName ?? undefined,
                              destinationName: route.toName ?? undefined,
                              tripType: trainForm.tripType,
                              totalSeatCount: 0, // fake
                          },
                          this.getInitialFilters(),
                          route.minPrice,
                          true
                      ),
                totalLength: params.expectedPackagesToBeSelected,
            });
        } catch (e) {
            reportException(e);
        }
    }

    override get metaTagTitle(): string {
        return `بلیط ${this.reserveInfo.title} ${this.originName} ${this.destinationName} (برنامه حرکت قطارها) - مِستربلیط`;
    }

    override getItineraryPackageLoaderConfigs(
        params: TrainUrlParams
    ): TrainPackageLoaderConfig[] {
        const trainForm = useTrainForm();
        const ret: TrainPackageLoaderConfig[] = [
            {
                originCode: params.originCode.toString(),
                destinationCode: params.destinationCode.toString(),
                date: params.departureDate,
                adultCount: params.adultCount || 1,
                childCount: params.childCount ?? 0,
                infantCount: params.infantCount ?? 0,
                sellType: params.sellTypeCode || SellTypeCode.Family,
                exclusive: params.exclusive ?? false,
                url: useRoute().fullPath,
                tripType: trainForm.tripType,
                originName: trainForm.origin?.title,
                destinationName: trainForm.destination?.title,
                totalSeatCount: 0, // fake
            },
        ];

        if (params.returnDate) {
            const loader: TrainPackageLoaderConfig = {
                originCode: params.destinationCode.toString(),
                destinationCode: params.originCode.toString(),
                date: params.returnDate,
                adultCount: params.adultCount || 1,
                childCount: params.childCount ?? 0,
                infantCount: params.infantCount ?? 0,
                sellType: params.sellTypeCode || SellTypeCode.Family,
                exclusive: params.exclusive ?? false,
                url: useRoute().fullPath,
                tripType: trainForm.tripType,
                originName: trainForm.destination?.title,
                destinationName: trainForm.origin?.title,
                totalSeatCount: 0, // fake
            };
            const [, returnItineraryPackageSet] = this.itineraryPackageSets;
            if (
                trainForm.tripType === TripType.RoundTrip &&
                this.isSelectingReturn &&
                returnItineraryPackageSet
            ) {
                loader.availableStatus =
                    returnItineraryPackageSet.availableStatus;
                loader.selectedCapacityId =
                    returnItineraryPackageSet.selectedCapacityId;
            }
            ret.push(loader);
        }

        return ret;
    }

    override beforeSelectPackage(params: {
        package: TrainPackage;
        index: number;
    }) {
        const trainForm = useTrainForm();
        if (
            trainForm.tripType === TripType.RoundTrip &&
            !!this.itineraryPackageSets?.[params.index + 1]
        ) {
            const packageSet = this.itineraryPackageSets[params.index + 1];
            if (params.package.isForRoundTrip && packageSet) {
                packageSet.availableStatus = GetAvailableStatus.OnlyRoundTrip;
                packageSet.selectedCapacityId =
                    params.package.selectedPrice!.id;
            } else if (
                packageSet &&
                packageSet.availableStatus !== GetAvailableStatus.OnlyOneWay
            ) {
                packageSet.availableStatus = GetAvailableStatus.OnlyOneWay;
                packageSet.selectedCapacityId = undefined;
            }
            if (params.package.mergedSegment) {
                this.itineraryPackageSets = this.itineraryPackageSets.slice(
                    0,
                    1
                );
            }
        }
    }

    override getInitialFilters(): { [key in TrainFiltersData['key']]: any } {
        return {
            corporation: null,
            hotel: null,
            price: null,
            time: null,
            wagonType: null,
        };
    }

    async getPackageMidStations(
        trainCapacityId: number
    ): Promise<MidStationResponse[]> {
        const { data } = await TrainApiApiMidStationTrainCapacityIdGet({
            trainCapacityId,
        });
        return data || [];
    }

    override formAutoReserve(): Promise<void> {
        return Promise.reject(new Error('Not implemented'));
    }

    override get alternativeSuggestionTypes(): [ReserveType, ReserveType] {
        return [ReserveType.Bus, ReserveType.Taxi];
    }
}
